/**
 * RFC 8785 (JSON Canonicalization Scheme) serializer and SHA-256 helper.
 * Editor preview, the server compile, Node tooling and tests all hash bytes produced here,
 * so identical values always yield identical digests.
 */
import { z } from 'zod';

export const sha256HexSchema = z.string().regex(/^[0-9a-f]{64}$/);

export class CanonicalJsonError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CanonicalJsonError';
  }
}

/** JCS text of `value`. Object members whose value is `undefined` are omitted, as in `JSON.stringify`. */
export function canonicalJson(value: unknown): string {
  return serializeValue(value, '$');
}

export function canonicalJsonBytes(value: unknown): Uint8Array {
  return new TextEncoder().encode(canonicalJson(value));
}

/** Lowercase hex SHA-256 via WebCrypto (browser, Node ≥ 18, jsdom with Node's `crypto`). */
export async function sha256Hex(bytes: Uint8Array | string): Promise<string> {
  const input = typeof bytes === 'string' ? new TextEncoder().encode(bytes) : bytes;
  const digest = await subtleCrypto().digest('SHA-256', input as BufferSource);
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

function subtleCrypto(): SubtleCrypto {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new CanonicalJsonError('WebCrypto SubtleCrypto is unavailable');
  return subtle;
}

function serializeValue(value: unknown, path: string): string {
  if (value === null) return 'null';
  switch (typeof value) {
    case 'boolean': return value ? 'true' : 'false';
    case 'string': return serializeString(value, path);
    case 'number': return serializeNumber(value, path);
    case 'object': return Array.isArray(value) ? serializeArray(value, path) : serializeObject(value as object, path);
    default: throw new CanonicalJsonError(`${path}: ${typeof value} is not JSON`);
  }
}

function serializeNumber(value: number, path: string): string {
  if (!Number.isFinite(value)) throw new CanonicalJsonError(`${path}: non-finite number`);
  // ECMAScript Number-to-String is exactly the JCS number form; -0 serializes as 0.
  return Object.is(value, -0) ? '0' : String(value);
}

function serializeString(value: string, path: string): string {
  for (let index = 0; index < value.length; index++) {
    const unit = value.charCodeAt(index);
    if (unit >= 0xd800 && unit <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) throw new CanonicalJsonError(`${path}: lone surrogate`);
      index++;
    } else if (unit >= 0xdc00 && unit <= 0xdfff) {
      throw new CanonicalJsonError(`${path}: lone surrogate`);
    }
  }
  return JSON.stringify(value);
}

function serializeArray(values: unknown[], path: string): string {
  return '[' + values.map((item, index) => {
    if (item === undefined) throw new CanonicalJsonError(`${path}[${index}]: undefined array element`);
    return serializeValue(item, `${path}[${index}]`);
  }).join(',') + ']';
}

function serializeObject(value: object, path: string): string {
  const prototype = Object.getPrototypeOf(value) as object | null;
  // Structured cloning across a Worker/iframe gives ordinary records that belong to another realm,
  // so their Object.prototype is not reference-equal to this realm's Object.prototype. A genuine
  // plain-object prototype still terminates directly at null; class/custom prototypes do not.
  const plain = prototype === null || prototype === Object.prototype || Object.getPrototypeOf(prototype) === null;
  if (!plain) {
    throw new CanonicalJsonError(`${path}: only plain objects are JSON`);
  }
  const record = value as Record<string, unknown>;
  // Default sort compares UTF-16 code units, which is the JCS member order.
  const keys = Object.keys(record).filter(key => record[key] !== undefined).sort();
  return '{' + keys.map(key => serializeString(key, path) + ':' + serializeValue(record[key], `${path}.${key}`)).join(',') + '}';
}
